import React, { useState } from 'react';
import { Package } from 'lucide-react';

interface LoginProps {
  onLogin: (username: string, password: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Trim the username to remove any whitespace
      await onLogin(username.trim(), password);
    } catch (err) {
      // Display the exact error message from the server
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3C3C3C] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-[#2C2C2C] rounded-lg shadow-xl p-8 w-full max-w-md border border-[#4C4C4C]">
          <div className="flex flex-col items-center mb-8">
            <img
              src="https://wholesale-images.stickysaguaro.com/products/StickyLogoWHRStacked.png"
              alt="Sticky Saguaro Logo"
              className="h-24 mb-4"
            />
            <h1 className="text-2xl font-bold text-white">Wholesale Manager</h1>
            <p className="text-gray-400">Sign in to your account</p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 bg-[#1C1C1C] border border-[#4C4C4C] rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-500"
                placeholder="Enter your username"
                required
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 bg-[#1C1C1C] border border-[#4C4C4C] rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-500"
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>

      <footer className="py-4 px-8 text-center text-gray-400">
        WASP Wholesale Manager V2 © 2025-{new Date().getFullYear()} - Sticky Saguaro
      </footer>
    </div>
  );
};

export default Login;